'use client'

import { useQuery } from '@apollo/client/react'
import { useTranslations } from 'next-intl'
import { GET_SOCIETY, SocietyDataType } from '@/graphql/queries/society'
import { usePathname } from '@/navigation'
import { StepsBreadcrumb } from '@/shared/components/steps-breadcrumb/steps-breadcrumb'
import { StepsProgress } from '@/shared/components/steps-breadcrumb/steps-progress'
import {
  BreadcrumbStep,
  breadcrumbItems,
  Navigation,
  pagesAfterPayment
} from '@/workflows/steps'
import { useNavigation } from '@/workflows/useNavigation'

// Steps shown in the breadcrumb: the applicable steps once the project type is known, none after the payment
const shownSteps = (
  navigation: Navigation,
  projectType: string | undefined,
  pathname: string
): BreadcrumbStep[] => {
  if (!projectType || pagesAfterPayment.includes(pathname)) return []
  return breadcrumbItems(navigation, pathname)
}

// Applicable steps of the prospect from its navigation, hidden before the project type is known and after the payment
const useBreadcrumbSteps = () => {
  const pathname = usePathname()
  const { navigation, projectType, goToStep } = useNavigation()
  return {
    steps: shownSteps(navigation, projectType, pathname),
    goToStep
  }
}

// Title of a step in the breadcrumb; the legal status one is followed by the legal status chosen
const useStepLabel = () => {
  const t = useTranslations('breadcrumb')
  const { data } = useQuery<SocietyDataType>(GET_SOCIETY)
  const legalStatus =
    data?.society?.legalStatus?.commonName || data?.society?.legalStatus?.name

  return (step: BreadcrumbStep) => {
    if (step.name !== 'legalStatus' || !legalStatus) return t(step.name)
    return t.rich('legalStatusChosen', {
      legalStatus,
      choice: (chunks) => <span className='block text-sm'>{chunks}</span>
    })
  }
}

// Applicable steps of the prospect, each one open up to the first step not complete, backward as forward
const Breadcrumb: React.FC<{ className?: string }> = ({ className }) => {
  const t = useTranslations('breadcrumb')
  const { steps, goToStep } = useBreadcrumbSteps()
  const stepLabel = useStepLabel()

  if (steps.length === 0) return null

  return (
    <StepsBreadcrumb
      aria-label={t('label')}
      className={className}
      items={steps.map((step) => ({
        key: step.name,
        label: stepLabel(step),
        state: step.state,
        onOpen: step.openable ? () => goToStep(step.path) : undefined
      }))}
    />
  )
}

// Position of the prospect among its applicable steps, shown instead of the breadcrumb on mobile
export const BreadcrumbProgress: React.FC<{ className?: string }> = ({
  className
}) => {
  const t = useTranslations('breadcrumb')
  const { steps } = useBreadcrumbSteps()
  const current = steps.findIndex(({ state }) => state === 'current')

  if (current === -1) return null

  return (
    <StepsProgress
      label={t('progress')}
      valueText={t(steps[current].name)}
      count={steps.length}
      current={current}
      className={className}
    />
  )
}

export default Breadcrumb
